import axios from "axios";
import { BASE_URL } from "../shared/constants";

// The manager portal's own axios instance and its own storage keys.
//
// Two reasons it cannot share the staff app's:
//
// 1. The staff 401->refresh interceptor and the Authorization default are set
//    inside App.js's mount effect (App.js:114-190). A tree that never renders
//    <App> inherits none of it.
// 2. App.js boot-checks auth by calling /customers/api/accounts/, which the
//    backend middleware now 403s for a manager. A manager token sitting under
//    `sfp_token` would leave the staff shell in a broken half-signed-in state.
//
// Separate keys also mean a staff session and a manager session can be open in
// two tabs at once, which is what makes the manual test bearable.

const TOKEN = "bm_token";
const REFRESH = "bm_refresh_token";
const USERNAME = "bm_username";

export const BM_API = `${BASE_URL}/api/bm`;

export const bm = axios.create({ baseURL: BM_API });

export function getToken() {
  return localStorage.getItem(TOKEN);
}

export function storeSession({ access, refresh, username }) {
  localStorage.setItem(TOKEN, access);
  if (refresh) localStorage.setItem(REFRESH, refresh);
  if (username) localStorage.setItem(USERNAME, username);
  bm.defaults.headers.common["Authorization"] = `Bearer ${access}`;
}

export function clearSession() {
  localStorage.removeItem(TOKEN);
  localStorage.removeItem(REFRESH);
  localStorage.removeItem(USERNAME);
  delete bm.defaults.headers.common["Authorization"];
}

export function storedUsername() {
  return localStorage.getItem(USERNAME) || "";
}

// Restore on module load so a refresh of /bmportal keeps you signed in.
const existing = getToken();
if (existing) {
  bm.defaults.headers.common["Authorization"] = `Bearer ${existing}`;
}

// Mirrors the staff interceptor: one retry per request, refresh, replay.
let onAuthLost = () => {};

export function setAuthLostHandler(fn) {
  onAuthLost = fn;
}

bm.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      const refresh = localStorage.getItem(REFRESH);
      if (refresh) {
        try {
          const res = await axios.post(`${BASE_URL}/auth/token/refresh/`, { refresh });
          const access = res.data.access;
          localStorage.setItem(TOKEN, access);
          bm.defaults.headers.common["Authorization"] = `Bearer ${access}`;
          original.headers = original.headers || {};
          original.headers["Authorization"] = `Bearer ${access}`;
          return bm(original);
        } catch {
          clearSession();
          onAuthLost();
        }
      } else {
        clearSession();
        onAuthLost();
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Sign in and confirm the account is actually a building manager.
 *
 * /auth/login/ cannot be trusted for this: its role derivation ends in a
 * fail-open `else: role = "field_tech"` (customers/api_views.py:144), so a
 * manager is labelled a field tech. The only honest check is whether
 * /api/bm/me/ answers.
 */
export async function bmLogin(username, password) {
  const res = await axios.post(
    `${BASE_URL}/auth/login/`,
    { username, password },
    { headers: { "Content-Type": "application/json" } }
  );
  storeSession({
    access: res.data.access,
    refresh: res.data.refresh,
    username: res.data.username,
  });
  try {
    const me = await bm.get("/me/");
    return me.data;
  } catch (e) {
    clearSession();
    const err = new Error("not-a-manager");
    err.code = "not-a-manager";
    throw err;
  }
}
